import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { v4 as uuidv4 } from "uuid";
import { Shield, ArrowUp, Sparkles, Share2 } from "lucide-react";
import { LeadForm } from "./LeadForm";
import { MagneticButton } from "./MagneticButton";
import { ToolCallCard } from "./ToolCallCard";
import { ShareAnswerModal } from "./ShareAnswerModal";
import { useAuthStore } from "../state/authStore";
import { API } from "../lib/api";
import { streamFetch } from "../lib/streamFetch";

const SUGGESTED_PROMPTS = [
    "Who are you?",
    "What services do you offer?",
    "Tell me about ZeroConf",
    "Book a consultation",
];

const SESSION_KEY = "cf_session_id";

function getOrCreateSessionId() {
    try {
        let id = window.localStorage.getItem(SESSION_KEY);
        if (!id) {
            id = uuidv4();
            window.localStorage.setItem(SESSION_KEY, id);
        }
        return id;
    } catch {
        return uuidv4();
    }
}

async function* parseSSE(response, signal) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    while (true) {
        if (signal?.aborted) {
            try {
                await reader.cancel();
            } catch {}
            return;
        }
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sepIdx;
        while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
            const rawFrame = buffer.slice(0, sepIdx);
            buffer = buffer.slice(sepIdx + 2);
            let eventName = "message";
            const dataLines = [];
            for (const line of rawFrame.split("\n")) {
                if (line.startsWith("event:")) {
                    eventName = line.slice(6).trim();
                } else if (line.startsWith("data:")) {
                    dataLines.push(line.slice(5).trim());
                }
            }
            if (dataLines.length === 0) continue;
            try {
                const data = JSON.parse(dataLines.join("\n"));
                yield { event: eventName, data };
            } catch {
                /* ignore */
            }
        }
    }
}

export const Chat = React.forwardRef(({ initialMessage = null }, ref) => {
    const [sessionId] = useState(() => getOrCreateSessionId());
    const [messages, setMessages] = useState([]); // {id, role, content, leadIntent?, toolEvents?, userPrompt?}
    const [streaming, setStreaming] = useState("");
    // Tool events for the in-flight assistant turn (rendered above the streaming text).
    const [streamingTools, setStreamingTools] = useState([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState("");
    const [input, setInput] = useState("");
    // Share-an-Answer modal (Phase D.3) — null when closed, else the assistant message snapshot.
    const [shareTarget, setShareTarget] = useState(null);
    const listRef = useRef(null);
    const abortRef = useRef(null);
    const currentUser = useAuthStore((s) => s.user);
    const isAuthed = !!currentUser;

    useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }, [messages, streaming]);

    const sendMessage = useCallback(
        async (text) => {
            const trimmed = (text ?? input).trim();
            if (!trimmed || isStreaming) return;
            setError("");

            const userMsg = { id: `u-${Date.now()}`, role: "user", content: trimmed };
            setMessages((prev) => [...prev, userMsg]);
            setInput("");
            setStreaming("");
            setStreamingTools([]);
            setIsStreaming(true);

            if (abortRef.current) abortRef.current.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            let accumulator = "";
            let leadIntent = null;
            // Local mutable map of toolCallId → tool event so we can merge
            // `tool_call` then `tool_result` into a single visual card.
            const toolEvents = [];
            const toolIndex = {};

            try {
                const res = await streamFetch(`${API}/chat`, {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "text/event-stream",
                    },
                    body: JSON.stringify({
                        session_id: sessionId,
                        message: trimmed,
                    }),
                    signal: controller.signal,
                });

                if (!res.ok) {
                    // Defensive: the preview environment's fetch wrapper may
                    // have already drained the body on non-2xx responses. We
                    // still want a clean error message, never a confusing
                    // "Body has already been consumed".
                    let body = "";
                    try {
                        body = await res.text();
                    } catch {
                        /* body already consumed by a fetch interceptor */
                    }
                    throw new Error(
                        `HTTP ${res.status} — ${body.slice(0, 200) || "request failed"}`,
                    );
                }
                if (!res.body) throw new Error("No streaming body in response");

                for await (const frame of parseSSE(res, controller.signal)) {
                    if (frame.event === "token" && frame.data?.token) {
                        accumulator += frame.data.token;
                        setStreaming(accumulator);
                    } else if (frame.event === "tool_call") {
                        const ev = {
                            id: frame.data?.id,
                            name: frame.data?.name,
                            args: frame.data?.args || {},
                            status: "running",
                        };
                        toolIndex[ev.id] = toolEvents.length;
                        toolEvents.push(ev);
                        setStreamingTools([...toolEvents]);
                    } else if (frame.event === "welcome" && frame.data?.text) {
                        // Phase D.3 — proactive plan-aware greeting on first turn.
                        // Persist immediately as a complete assistant message so
                        // it survives the streaming-bubble teardown at end of turn.
                        const welcomeId = `w-${Date.now()}`;
                        setMessages((prev) => [
                            ...prev,
                            {
                                id: welcomeId,
                                role: "assistant",
                                content: frame.data.text,
                                type: "welcome",
                            },
                        ]);
                    } else if (frame.event === "tool_result") {
                        const idx = toolIndex[frame.data?.id];
                        if (idx != null) {
                            toolEvents[idx] = {
                                ...toolEvents[idx],
                                status: frame.data?.success ? "success" : "error",
                                result: frame.data?.result,
                                error: frame.data?.error,
                                errorCode: frame.data?.error_code,
                                userMessage: frame.data?.user_message,
                                redirectUrl: frame.data?.redirect_url,
                                durationMs: frame.data?.duration_ms,
                            };
                            setStreamingTools([...toolEvents]);
                        }
                    } else if (frame.event === "lead_form" && frame.data?.intent) {
                        leadIntent = frame.data.intent;
                    } else if (frame.event === "error") {
                        throw new Error(frame.data?.message || "Unknown stream error");
                    } else if (frame.event === "done") {
                        break;
                    }
                }

                if (accumulator.trim() || toolEvents.length) {
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: `a-${Date.now()}`,
                            role: "assistant",
                            content: accumulator,
                            leadIntent,
                            userPrompt: trimmed,
                            toolEvents: toolEvents.length ? toolEvents : undefined,
                        },
                    ]);
                }
            } catch (e) {
                if (e?.name === "AbortError") {
                    if (accumulator.trim() || toolEvents.length) {
                        setMessages((prev) => [
                            ...prev,
                            {
                                id: `a-${Date.now()}`,
                                role: "assistant",
                                content: accumulator,
                                leadIntent,
                                userPrompt: trimmed,
                                toolEvents: toolEvents.length ? toolEvents : undefined,
                            },
                        ]);
                    }
                } else {
                    setError(e?.message || "Something went wrong");
                }
            } finally {
                setStreaming("");
                setStreamingTools([]);
                setIsStreaming(false);
                abortRef.current = null;
            }
        },
        [input, isStreaming, sessionId],
    );

    // Expose sendMessage to parent via ref so the homepage demo strip can
    // forward a prompt directly into the chat without simulating keystrokes.
    useImperativeHandle(ref, () => ({ sendMessage }), [sendMessage]);

    // Auto-send initialMessage once if provided (used by deep links like "Book a consultation")
    const sentInitial = useRef(false);
    useEffect(() => {
        if (initialMessage && !sentInitial.current) {
            sentInitial.current = true;
            // small delay so the user sees the chat first
            const t = setTimeout(() => sendMessage(initialMessage), 350);
            return () => clearTimeout(t);
        }
    }, [initialMessage, sendMessage]);

    const dismissLead = (msgId) => {
        setMessages((prev) =>
            prev.map((m) => (m.id === msgId ? { ...m, leadIntent: null } : m)),
        );
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        sendMessage();
    };

    const showEmpty = messages.length === 0 && !streaming && !isStreaming;

    return (
        <div className="relative w-full max-w-2xl mx-auto" data-testid="chat-container">
            <div className="relative rounded-2xl border border-white/[0.06] bg-surface/80 backdrop-blur-md shadow-glow-cyan overflow-hidden">
                <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/[0.04]">
                    <div className="flex items-center gap-2">
                        <span className="relative inline-flex h-2 w-2">
                            <span className="absolute inset-0 rounded-full bg-accent-cyan opacity-60 animate-pulse-cyan" />
                            <span className="relative rounded-full h-2 w-2 bg-accent-cyan" />
                        </span>
                        <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-text-muted">
                            Concierge / Online
                        </span>
                    </div>
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-text-dim">
                        gpt-4o-mini
                    </span>
                </div>

                <div
                    ref={listRef}
                    className="scroll-thin px-5 py-6 min-h-[300px] max-h-[58vh] overflow-y-auto"
                    data-testid="chat-message-list"
                >
                    {showEmpty && (
                        <div className="text-left space-y-3">
                            <div className="flex items-start gap-2.5">
                                <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-md bg-accent-cyan/10 border border-accent-cyan/20">
                                    <Shield className="w-3.5 h-3.5 text-accent-cyan" />
                                </span>
                                <p
                                    className="text-[14px] leading-relaxed text-text-muted"
                                    data-testid="chat-greeting"
                                >
                                    Hi — I&apos;m the Cybersecurity Forefront concierge. Ask
                                    me anything about our services, or type
                                    &ldquo;ZeroConf&rdquo; to learn about our flagship product.
                                </p>
                            </div>
                        </div>
                    )}

                    <AnimatePresence initial={false}>
                        {messages.map((m) => (
                            <React.Fragment key={m.id}>
                                {m.role === "user" ? (
                                    <motion.div
                                        initial={{ opacity: 0, y: 6, filter: "blur(4px)" }}
                                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                                        transition={{ duration: 0.25, ease: "easeOut" }}
                                        className="my-3 flex justify-end"
                                        data-testid="message-bubble-user"
                                    >
                                        <div className="max-w-[85%] rounded-2xl rounded-br-md px-4 py-2.5 bg-accent-cyan/10 border border-accent-cyan/25 text-[14px] leading-relaxed text-text-primary">
                                            {m.content}
                                        </div>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        initial={{ opacity: 0, y: 6, filter: "blur(4px)" }}
                                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                                        transition={{ duration: 0.3, ease: "easeOut" }}
                                        className="my-3 flex items-start gap-2.5"
                                        data-testid="message-bubble-ai"
                                    >
                                        <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-md bg-accent-cyan/10 border border-accent-cyan/20 shrink-0">
                                            <Shield className="w-3.5 h-3.5 text-accent-cyan" />
                                        </span>
                                        <div className="max-w-[85%] flex-1 min-w-0">
                                            {m.toolEvents?.map((ev) => (
                                                <ToolCallCard
                                                    key={ev.id}
                                                    name={ev.name}
                                                    args={ev.args}
                                                    status={ev.status}
                                                    result={ev.result}
                                                    error={ev.error}
                                                    errorCode={ev.errorCode}
                                                    userMessage={ev.userMessage}
                                                    redirectUrl={ev.redirectUrl}
                                                    durationMs={ev.durationMs}
                                                />
                                            ))}
                                            {m.content && (
                                                <div className="text-[14px] leading-relaxed text-text-primary whitespace-pre-wrap">
                                                    {m.content}
                                                </div>
                                            )}
                                            {/* Share button — Phase D.3 — only for signed-in users
                                                AND only when this assistant turn has at least one
                                                tool result. Auth-gated content is filtered server-side. */}
                                            {isAuthed && m.toolEvents?.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setShareTarget(m)}
                                                    className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-mono uppercase tracking-wider text-text-dim hover:text-accent-cyan transition-colors"
                                                    data-testid={`share-button-${m.id}`}
                                                    aria-label="Share this answer"
                                                >
                                                    <Share2 className="w-3 h-3" />
                                                    Share
                                                </button>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                                {m.role === "assistant" && m.leadIntent && (
                                    <LeadForm
                                        intent={m.leadIntent}
                                        sessionId={sessionId}
                                        onClose={() => dismissLead(m.id)}
                                    />
                                )}
                            </React.Fragment>
                        ))}
                    </AnimatePresence>

                    {isStreaming && (
                        <div
                            className="my-3 flex items-start gap-2.5"
                            data-testid="message-bubble-ai-streaming"
                        >
                            <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-md bg-accent-cyan/10 border border-accent-cyan/20 shrink-0">
                                <Shield className="w-3.5 h-3.5 text-accent-cyan" />
                            </span>
                            <div className="max-w-[85%] flex-1 min-w-0 text-[14px] leading-relaxed text-text-primary whitespace-pre-wrap">
                                {streamingTools.map((ev) => (
                                    <ToolCallCard
                                        key={ev.id}
                                        name={ev.name}
                                        args={ev.args}
                                        status={ev.status}
                                        result={ev.result}
                                        error={ev.error}
                                        errorCode={ev.errorCode}
                                        userMessage={ev.userMessage}
                                        redirectUrl={ev.redirectUrl}
                                        durationMs={ev.durationMs}
                                    />
                                ))}
                                {streaming ? (
                                    <>
                                        {streaming}
                                        <span className="ml-0.5 inline-block w-1.5 h-3.5 align-middle bg-accent-cyan/80 animate-pulse-cyan rounded-sm" />
                                    </>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5">
                                        <span
                                            className="h-1.5 w-1.5 rounded-full bg-accent-cyan animate-pulse-cyan"
                                            style={{ animationDelay: "0ms" }}
                                        />
                                        <span
                                            className="h-1.5 w-1.5 rounded-full bg-accent-cyan animate-pulse-cyan"
                                            style={{ animationDelay: "150ms" }}
                                        />
                                        <span
                                            className="h-1.5 w-1.5 rounded-full bg-accent-cyan animate-pulse-cyan"
                                            style={{ animationDelay: "300ms" }}
                                        />
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {error && (
                        <div
                            className="my-3 text-[12px] font-mono text-accent-red bg-accent-red/5 border border-accent-red/20 rounded-md px-3 py-2"
                            data-testid="chat-error"
                        >
                            {error}
                        </div>
                    )}
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="flex items-center gap-2 border-t border-white/[0.04] bg-canvas/60 px-3 py-3 focus-within:bg-canvas/80 focus-within:shadow-glow-cyan transition-shadow duration-300"
                    data-testid="chat-input-form"
                >
                    <Sparkles className="w-4 h-4 text-text-dim ml-1.5 shrink-0" />
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Ask anything…"
                        disabled={isStreaming}
                        className="flex-1 bg-transparent outline-none text-[14px] text-text-primary placeholder:text-text-dim disabled:opacity-50"
                        data-testid="chat-input"
                        aria-label="Ask the concierge"
                    />
                    <MagneticButton
                        type="submit"
                        disabled={!input.trim() || isStreaming}
                        radius={70}
                        strength={0.22}
                        className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-accent-cyan text-canvas hover:shadow-glow-cyan disabled:opacity-30 disabled:cursor-not-allowed transition-shadow duration-200"
                        data-testid="chat-send-button"
                        ariaLabel="Send message"
                    >
                        <ArrowUp className="w-4 h-4" />
                    </MagneticButton>
                </form>
            </div>

            {showEmpty && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-4 flex flex-wrap gap-2 justify-center"
                    data-testid="prompt-chips"
                >
                    {SUGGESTED_PROMPTS.map((p) => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => sendMessage(p)}
                            className="text-[12px] font-mono text-text-muted px-3 py-1.5 rounded-md bg-elevated/50 border border-white/[0.06] hover:border-accent-cyan/40 hover:text-accent-cyan hover:bg-accent-cyan/[0.06] transition-colors duration-200"
                            data-testid={`prompt-chip-${p.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                            {p}
                        </button>
                    ))}
                </motion.div>
            )}

            {/* Share-an-Answer modal (Phase D.3) */}
            <ShareAnswerModal
                open={!!shareTarget}
                onClose={() => setShareTarget(null)}
                message={shareTarget}
                userPrompt={shareTarget?.userPrompt}
            />
        </div>
    );
});

Chat.displayName = "Chat";

export default Chat;
